import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { WallboardViewService } from '../wallboard-view.service';
import { CurrentAgentStatesModuleDataObj, WVBase, WVMain } from '../wallboard-view.model';
import { CurrentAgentStatesSettingTransferObj, ModuleType } from '../../wallboards.model';

@Component({
    selector: 'cv-wallboard-view-current-agent-states',
    templateUrl: './wallboard-view-current-agent-states.component.html',
    styleUrls: ['./wallboard-view-current-agent-states.component.scss']
})
export class WallboardViewCurrentAgentStatesComponent implements OnInit, OnDestroy {
    @Input() firstSlide: boolean;
    public wvf: WVMain;
    public wvcasf: WVBase;
    public currentAgentStatesState: CurrentAgentStatesSettingTransferObj;
    public currentAgentStatesData: CurrentAgentStatesModuleDataObj;
    filledOnCall: CurrentAgentStatesModuleDataObj[] = [];
    filledUnavailable: CurrentAgentStatesModuleDataObj[] = [];
    filledAvailable: CurrentAgentStatesModuleDataObj[] = [];

    private dataUpdated: boolean = false;
    public tableHeight: number = 0;

    private updateTimerRef:NodeJS.Timeout = null;
    private isDestroyed: boolean = false;
    private slideActivationSubscription: NodeJS.Timeout | null = null;

    public paddedData = [[], [], []];

    // flag for background image ID slides
    private _backgroundID: number = 0;

    get hasBackdrop(): boolean {
        return this.wvcasf?.Slide.HasBackdrop ?? false;
    }

    get moduleKey(): string {
        return this.wvcasf?.Slide?.WallboardModuleDetails?.[0]?.WallboardModuleKey ?? "";
    }

    get backgroundID(): number {
        return this._backgroundID;
    }

    constructor(
        private service: WallboardViewService,
        private cdr: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.wvf = this.service.WVMain;
        this.wvcasf = this.service.WVAgentStates;
        this._backgroundID = this.wvcasf?.Slide.BackgroundImageID ?? 0;

        // Seed from localStorage cache if a recent successful response exists for
        // this wallboard+module. Lets a freshly-created instance (e.g. after slide
        // rotation destroys the previous one) render the last known values
        // immediately, instead of showing zero defaults while its own initial API
        // call is in flight. Guarded against missing slide config so we don't end
        // up writing garbage cache keys like "cv-cas-cache:undefined:" to localStorage.
        const wallboardId = this.wvcasf?.Slide?.WallboardID;
        const cacheModuleKey = this.moduleKey;
        if (wallboardId && cacheModuleKey) {
            const cached = this.service.readCachedAgentStatesData(wallboardId, cacheModuleKey);
            if (cached) {
                this.currentAgentStatesData = cached;
                this.updateFilledColumns();
            }
        }

        this.wvf.RunSlide = this.firstSlide;
        if (!this.firstSlide)
            this.wvf.NextSlideReady = false;

        let moduleState = this.service.loadModuleState(ModuleType.CurrentAgentStates, this.wvf.WallboardCreatorID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.currentAgentStatesState = response.currentAgentStatesObj;
        });

        if (this.firstSlide) {
            // For first slide, load data immediately
            this.loadSlideDataAndStart(moduleState);
        } else {
            // For preloaded slides, just wait for module state then mark ready
            moduleState.then(() => {
                if (!this.isDestroyed) {
                    this.wvf.NextSlideReady = true;
                }
            });

            // Subscribe to slide activation
            this.subscribeToSlideActivation();
        }
    }

    private async loadSlideDataAndStart(moduleStatePromise?: Promise<any>): Promise<void> {
        try {
            // Wait for module state if provided
            if (moduleStatePromise) {
                await moduleStatePromise;
            }

            if (this.isDestroyed) return;

            // Load the actual data
            const response = await this.service.getCurrentAgentStatesModuleData(
                this.wvcasf.Slide.WallboardID,
                this.moduleKey
            );
            if (response) {
                this.currentAgentStatesData = response;
            }

            this.updateFilledColumns();

            if (this.firstSlide) {
                this.wvf.FirstSlideReady = true;
            }

            let $viewSlide = angular.element(document).find('.wallboardViewSlide');
            $viewSlide.height('100vh');
            this.tableHeight = $viewSlide.height() - 132;

            this.runSlide();

        } catch (error) {
            console.error('Error loading slide data:', error);
        }
    }

    private subscribeToSlideActivation(): void {
        // Check periodically if this slide became active
        const checkActivation = () => {
            if (this.isDestroyed) return;

            if (this.wvf.RunSlide && !this.dataUpdated) {
                // Slide just became active and the 5s refresh interval hasn't been
                // wired up yet, so fire the initial load. We check `dataUpdated` (set
                // by runSlide once the interval is set up) instead of the data field
                // because the cache seed in ngOnInit may have already populated
                // currentAgentStatesData — and without this distinction, a cache hit
                // would block loadSlideDataAndStart from ever firing, leaving the
                // wallboard with frozen cached values and no refresh.
                this.loadSlideDataAndStart();
            } else if (!this.wvf.RunSlide) {
                // Still waiting, check again
                this.slideActivationSubscription = setTimeout(checkActivation, 500);
            }
        };

        checkActivation();
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;

        // End data updates
        if (this.updateTimerRef != null) {
            clearInterval(this.updateTimerRef);
            this.updateTimerRef = null;
        }

        // Clean up subscription
        if (this.slideActivationSubscription) {
            clearTimeout(this.slideActivationSubscription);
            this.slideActivationSubscription = null;
        }

        // Clear data to prevent memory leaks
        this.currentAgentStatesData = null;
        this.filledOnCall = [];
        this.filledUnavailable = [];
        this.filledAvailable = [];
    }

    updateFilledColumns(): void {
        this.filledOnCall = this.fillColumn(0, this.currentAgentStatesData?.OnCall || [], ['callDirection', 'groupName', 'stateName', 'stateTime']);
        this.filledUnavailable = this.fillColumn(1, this.currentAgentStatesData?.Unavailable || [], ['groupName', 'stateName', 'stateTime', 'isACW']);
        this.filledAvailable = this.fillColumn(2, this.currentAgentStatesData?.Available || [], ['groupName', 'stateTime']);
    }

    getLimitTo(index) {
        let conHeight = $($(".wvCurrentAgentStatesColumn")[index]).height() - $($(".wvCurrentAgentStatesColumnTitle")[index]).outerHeight();
        let rowHeight = $($(".wvCurrentAgentStatesGridCell")[index]).outerHeight();

        if (conHeight < rowHeight)
            conHeight = $($(".wvCurrentAgentStatesColumn")[index]).height() - rowHeight;

        let rows = conHeight / rowHeight;
        return rows % 1 < 0.75 ? Math.floor(rows) - 1 : Math.floor(rows);
    }

    /**
     * Used to fill the ng-repeats with blank data so the css grid doesn't center them vertically when there aren't enough rows.
     * @param index Index of box to find max size for; 0 for OnCall, 1 for Unavailable, 2 for Available
     * @param data Data that is being repeated over
     * @param includedProperties List of properties to include in the blank data. All of these will be set to empty string ("")
     */
    fillColumn(index: number, data: CurrentAgentStatesModuleDataObj[], includedProperties: string[]): CurrentAgentStatesModuleDataObj[] {
        if (!data || !this.currentAgentStatesData)
            return [];

        let maxDataRows = this.getLimitTo(index);
        if (isNaN(maxDataRows) || maxDataRows === Infinity)
            maxDataRows = 10;

        let paddedData = angular.copy(data);

        let padding: CurrentAgentStatesModuleDataObj = {
            stateName:"",
            stateTime:"",
            callDirection:"",
            groupName:"",
            isACW:"",
            OnCall: [],
            Unavailable: [],
            Available: [],
            InboundCount: 0,
            OutboundCount: 0,
            UnavailableCount: 0,
            AvailableCount: 0,
            TotalCount: 0,
            CallsInQueueCount: 0,
            LongestCallInQueue: null,
            LongestCallInQueueFormatted: "",
            AgentStateCategories: new Map<string, string>()
        };

        // Pad with empty rows only when there is unused vertical space so the CSS grid
        // doesn't vertically center sparse columns. Never truncate real agents — when
        // there are more agents than the measured row capacity, render them all so the
        // header count (e.g. Unavailable 31) always matches the rows visible in the column.
        if (paddedData.length < maxDataRows) {
            for (let i = 0; i < includedProperties.length; i++) {
                padding[includedProperties[i]] = "";
            }
            while (paddedData.length < maxDataRows) {
                paddedData.push(padding);
            }
        }

        return paddedData;
    }

    async runSlide() {
        // Don't start data refresh timer until we have data and slide is running
        if (!this.dataUpdated && this.wvf.RunSlide && this.currentAgentStatesData) {
            // Reload the data every 5 secs - but keep running until slide actually ends
            this.updateTimerRef = setInterval(() => {
                // Only update data if slide is still supposed to be running
                // Don't stop just because transition is being prepared
                if (this.wvf.RunSlide && !this.isDestroyed) {
                    this.refreshSlideData();
                }
            }, WallboardViewService.REALTIME_DATA_REFRESH_INTERVAL);

            this.dataUpdated = true;
        }

        if (this.wvf.RunSlide && this.currentAgentStatesData) {
            if (this._backgroundID === 0) {
                this._backgroundID = this.wvcasf?.Slide.BackgroundImageID ?? 0;
            }

            setTimeout(() => {
                // Was originally this.currentAgentStatesData.length, but that makes no sense because this.currentAgentStatesData is not an array or other collection.
                // This is the closest thing that even approximates something coherent.
                for (let i = 0; i < (this.currentAgentStatesData?.OnCall?.length ?? 0); i++) {
                    this.animateRow(0);
                }
                this.cdr.detectChanges();
            }, 0);
        }
        else {
            setTimeout(() => {
                this.runSlide();
            }, 1000);
        }
    }

    animateRow(idx) {
        setTimeout(() => {
            $(".wallboardViewSlide[mid=" + this.moduleKey + "]:last").find("#row" + idx).fadeIn(1000);
        }, 500 * idx);
    }

    async refreshSlideData() {
        let response = await this.service.getCurrentAgentStatesModuleData(this.wvcasf.Slide.WallboardID, this.moduleKey);
        if (this.isDestroyed) return;
        // Retain the previously-rendered values if the refresh errored or was
        // cancelled (service swallows errors and returns undefined). Otherwise
        // dump the response straight into the field — no field-level filtering.
        if (!response) return;
        this.currentAgentStatesData = response;
        this.updateFilledColumns();
    }
}
